import { useMemo } from "react";

// Types for the hook
export interface ResponsiveTextContent {
  textXS?: string;
  textSM?: string;
  textMD?: string;
  textLG?: string;
  textXL?: string;
}

export interface BreakpointConfig {
  key: string;
  text?: string;
  className: string;
}

export interface ResponsiveContentItem {
  key: string;
  content: string;
  className: string;
}

/**
 * Custom hook for managing responsive content with fallback logic
 * Handles content generation with functional programming patterns
 */
export const useResponsiveTextContent = (
  responsiveContent: ResponsiveTextContent
) => {
  const responsiveContentItems = useMemo(() => {
    const { textXS, textSM, textMD, textLG, textXL } = responsiveContent;

    // Define breakpoints configuration
    const breakpoints: BreakpointConfig[] = [
      { key: "xs", text: textXS, className: "text-content-xs" },
      { key: "sm", text: textSM, className: "text-content-sm" },
      { key: "md", text: textMD, className: "text-content-md" },
      { key: "lg", text: textLG, className: "text-content-lg" },
      { key: "xl", text: textXL, className: "text-content-xl" }
    ];

    // Create a map of available content for fallback lookup
    const availableContentMap = new Map<string, string>();
    breakpoints.forEach(breakpoint => {
      if (breakpoint.text) {
        availableContentMap.set(breakpoint.key, breakpoint.text);
      }
    });

    // Generate responsive content items with fallback logic
    const fallbackOrder = ["xs", "sm", "md", "lg", "xl"];

    return breakpoints
      .map(breakpoint => {
        // Find fallback content by looking backwards through the order
        const currentIndex = fallbackOrder.indexOf(breakpoint.key);

        // Use functional approach to find the first available content
        const fallbackContent = fallbackOrder
          .slice(0, currentIndex + 1) // Get all breakpoints up to current
          .reverse() // Start from current and go backwards
          .map(fallbackKey => availableContentMap.get(fallbackKey))
          .find(content => content); // Find first truthy content

        // Return content item if we found fallback content
        return fallbackContent
          ? {
              key: breakpoint.key,
              content: fallbackContent,
              className: breakpoint.className
            }
          : null;
      })
      .filter((item): item is ResponsiveContentItem => item !== null);
  }, [responsiveContent]);

  return responsiveContentItems;
};
